import { useEffect, useRef, useState } from 'react';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ToggleButton from 'components/buttons/ToggleButton';

import useTranslation from './hooks/useTranslation';

import { CONTENT_HEIGHT_VARIABLE, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const Drawer: FunctionComponent<Props> = ({ children, className = '', testId = TEST_ID }) => {
    const { CLOSE_TEXT, OPEN_TEXT } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const drawerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const drawer = drawerRef.current;
        const content = contentRef.current;

        if (!drawer || !content) {
            return;
        }

        const updateHeight = () => {
            drawer.style.setProperty(CONTENT_HEIGHT_VARIABLE, `${content.offsetHeight}px`);
        };

        updateHeight();

        const observer = new ResizeObserver(updateHeight);

        observer.observe(content);

        return () => observer.disconnect();
    }, []);

    const handleToggle = () => setIsOpen(previousIsOpen => !previousIsOpen);

    const openClass = isOpen ? styles.open : '';
    const toggleText = isOpen ? CLOSE_TEXT : OPEN_TEXT;

    return (
        <VerticalContainer
            className={`${className} ${styles.drawer}`}
            ref={drawerRef}
            testId={testId}>
            <ToggleButton
                className={styles.toggleButton}
                onClick={handleToggle}
                text={toggleText}
            />

            <Wrapper className={`${openClass} ${styles.wrapper}`}>
                <Wrapper className={styles.content} ref={contentRef}>
                    {children}
                </Wrapper>
            </Wrapper>
        </VerticalContainer>
    );
};

export default Drawer;
